import { useState } from 'react'
import { toast } from 'react-toastify'
import { PiTrash } from 'react-icons/pi'

import useApiService from 'services/api'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { DsInput } from 'components/FbUI/Ds2026Field'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TabPanel } from 'components/FbUI/PillTabs'
import { cn } from 'components/UI/cn'

// Keep in sync with MAX_USER_RECOMMENDATION_NOTES and the content max_length in
// server/recommendations/models.py (UserRecommendationNote).
const MAX_NOTES = 10
const MAX_NOTE_LENGTH = 1000

// Keep in sync with UserRecommendationNoteCategory
// (server/recommendations/models.py).
const NOTE_CATEGORIES = [
  {
    value: 'global',
    label: 'Global',
    placeholder:
      'Add a note, e.g. "We sell plant-based proteins to K-12 districts"',
  },
  {
    value: 'work_role',
    label: 'Your Role',
    placeholder:
      'Add a note, e.g. "I\'m a regional sales manager covering the Northeast"',
  },
  {
    value: 'message_tone',
    label: 'Message Tone',
    placeholder: 'Add a note, e.g. "Keep outreach warm but concise"',
  },
] as const

type NoteCategory = (typeof NOTE_CATEGORIES)[number]['value']

// Unique prefix wiring the UnderlineTabs tabs to their TabPanel for a11y.
const NOTES_TAB_ID = 'recommendation-notes'

/**
 * Dynamic per-user recommendation notes ("memories", FIR-7479), organized by
 * category tabs (Global / Your Role / Message Tone). Users add/remove
 * free-form notes that feed into recommendation synthesis. Shared by the AI
 * settings page (self) and the admin Users-detail accordion (`userId` targets
 * another user).
 *
 * @param userId - Target user for admin editing; omit to edit your own notes.
 */
export function RecommendationNotesEditor({ userId }: { userId?: number }) {
  const api = useApiService()
  const { data: notes, isLoading } = api.useGetRecommendationNotes(userId)
  const addNote = api.useAddRecommendationNote(userId)
  const deleteNote = api.useDeleteRecommendationNote(userId)
  const [draft, setDraft] = useState('')
  const [activeCategory, setActiveCategory] = useState<NoteCategory>(
    NOTE_CATEGORIES[0].value
  )
  // Track in-flight deletes per note id so one delete doesn't disable every
  // other card's remove button (deleteNote is a single shared mutation).
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())
  // Notes collapse to 2 lines by default; clicking one toggles it open so long
  // notes don't blow out the list.
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allNotes = notes ?? []
  const visibleNotes = allNotes.filter((n) => n.category === activeCategory)
  const activeCategoryConfig =
    NOTE_CATEGORIES.find((cat) => cat.value === activeCategory) ??
    NOTE_CATEGORIES[0]
  // Per-tab count badges (null hides the badge for empty categories).
  const categoryTabs = NOTE_CATEGORIES.map((cat) => ({
    key: cat.value,
    title: cat.label,
    count: allNotes.filter((n) => n.category === cat.value).length || null,
  }))
  // The 10-note cap is across all categories (bounds the synthesis prompt).
  const atCap = allNotes.length >= MAX_NOTES
  const canAdd = draft.trim().length > 0 && !atCap && !addNote.isPending

  const handleAdd = async () => {
    if (!canAdd) return
    try {
      await addNote.mutateAsync({
        content: draft.trim(),
        category: activeCategory,
      })
      setDraft('')
    } catch {
      toast.error('Failed to add note')
    }
  }

  const handleDelete = async (id: number) => {
    setDeletingIds((prev) => new Set(prev).add(id))
    try {
      await deleteNote.mutateAsync(id)
    } catch {
      toast.error('Failed to remove note')
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  return (
    <div>
      <UnderlineTabs
        tabs={categoryTabs}
        activeKey={activeCategory}
        onChange={setActiveCategory}
        idPrefix={NOTES_TAB_ID}
        aria-label="Note category"
        className="mb-3"
      />

      <TabPanel
        idPrefix={NOTES_TAB_ID}
        tabKey={activeCategory}
        activeKey={activeCategory}
      >
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <DsInput
              type="text"
              value={draft}
              maxLength={MAX_NOTE_LENGTH}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleAdd()
                }
              }}
              placeholder={activeCategoryConfig.placeholder}
              disabled={atCap}
              aria-label="Add a note"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-neutral-500">
                {atCap ? `Limit of ${MAX_NOTES} notes reached` : ''}
              </p>
              <p className="text-xs text-neutral-500">
                {draft.length}/{MAX_NOTE_LENGTH}
              </p>
            </div>
          </div>
          <Ds2026ButtonProvider>
            <FbButton
              type="button"
              variant="primary"
              onClick={handleAdd}
              disabled={!canAdd}
              loading={addNote.isPending}
            >
              Add
            </FbButton>
          </Ds2026ButtonProvider>
        </div>

        <div className="mt-3 space-y-2">
          {isLoading ? (
            <p className="text-sm text-neutral-500">Loading…</p>
          ) : visibleNotes.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">
              No notes in this category yet.
            </p>
          ) : (
            visibleNotes.map((note) => {
              const expanded = expandedIds.has(note.id)
              return (
                <div
                  key={note.id}
                  className="flex items-start justify-between gap-3 rounded-lg bg-neutral-100 px-3.5 py-2"
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(note.id)}
                    aria-expanded={expanded}
                    className={cn(
                      'min-w-0 flex-1 break-words text-left text-sm text-neutral-800',
                      expanded ? 'whitespace-pre-wrap' : 'line-clamp-2'
                    )}
                  >
                    {note.content}
                  </button>
                  <button
                    type="button"
                    aria-label="Remove note"
                    onClick={() => handleDelete(note.id)}
                    disabled={deletingIds.has(note.id)}
                    className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 disabled:opacity-50"
                  >
                    <PiTrash size={16} />
                  </button>
                </div>
              )
            })
          )}
        </div>
      </TabPanel>
    </div>
  )
}
